import React, { useState } from 'react';
import { Skeleton } from 'antd';
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Task } from 'models/CommonType';
import { truncateString } from "utils/helpers";
import './SkeletonItem.css'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px;
  margin: 0;
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  /* No image state - expand text area and add better padding */
  &.no-image {
    padding: 20px;
  }

  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 8px;
    
    &.no-image {
      padding: 12px;
    }
  }
`;

const ImageContainer = styled.div`
  padding: 0;
  background-color: #F5F5F5;
  flex: 0 0 80px;
  width: 80px;
  height: 80px;
  max-width: 80px;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 16px;
  aspect-ratio: 1;
  
  @media (max-width: 768px) {
    flex: 0 0 60px;
    width: 60px;
    height: 60px;
    max-width: 60px;
    border-radius: 6px;
    margin-right: 12px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const TextContainer = styled.div`
  padding: 0;
  flex: 1;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
`;

const Name = styled.span`
  line-height: 1.4;
  font-size: 0.95rem;
  margin-bottom: 4px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.3;
    margin-bottom: 2px;
  }
`;

const Description = styled.span`
  color: gray;
  font-size: 0.8rem;
  line-height: 1.3;
  -webkit-line-clamp: 2;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.2;
    margin-bottom: 6px;
    -webkit-line-clamp: 2;
  }
`;

const Meta = styled.span`
  color: #60865B;
  font-size: 0.75rem;
  line-height: 1.2;
  margin-top: auto;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

interface Props {
  item?: Task;
  closeHandler?: () => void;
}

const TaskItem: React.FC<Props> = ({
  item,
  closeHandler,
}) => {
  const { t } = useTranslation();
  const [hasImage, setHasImage] = useState(!!item?.image_url);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setHasImage(false);
    setImageLoaded(false);
  };

  if (!item) {
    return (
      <Container>
        <ImageContainer className='SkeletonImg'>
          <Skeleton.Image active />
        </ImageContainer>
        <TextContainer className='SkeletonText'>
          <Skeleton active />
        </TextContainer>
      </Container>
    );
  }

  const showImage = hasImage && item.image_url;

  return (
    <Link to={`/task?id=${item.id}`} onClick={closeHandler}>
      <Container className={!showImage ? 'no-image' : ''}>
        {showImage && (
          <ImageContainer>
            <Image 
              src={item.image_url} 
              alt={item.name}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ opacity: imageLoaded ? 1 : 0.7 }}
            />
          </ImageContainer>
        )}

        <TextContainer className="text-container">
          <div>
            <Name>{item.name}</Name>
            <Description>{truncateString(item.description, showImage ? 80 : 120)}</Description>
          </div>
          <Meta>{item.skill?.name} • {item.miles} {t('common.mile', { count: item.miles })}</Meta>
        </TextContainer>
      </Container>
    </Link>
  );
}

export default TaskItem;
